import Member from 'iam/models/Member'
import TeamMember from 'iam/models/TeamMember'
import Workspace from 'iam/models/Workspace'
import { GetCurrentMember } from 'iam/SessionStore'
import { GetTeamMember } from 'iam/TeamMemberStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { computed, observable, makeObservable } from 'mobx'
import { once } from 'lodash'
import { Model } from 'infra/model'
import { BillingProfileDTO } from 'billing/dtos/billing-profile'
import { maybeParseDate, DateZero } from 'utils/date-util'
import { collection } from 'infra/collection'
import { BillingMethodDTO } from 'billing/dtos/billing-method'
import BillingMethod from './BillingMethod'
import { Id } from 'types/definitions'

export default class BillingProfile extends Model<
  BillingProfileDTO,
  BillingProfile
> {
  id = Id.none
  userId: Id | null = Id.none
  userPublicId: Id | null = Id.none
  teamId: Id | null = Id.none
  referralCouponCode = ''
  referralCouponId = Id.none

  @observable dateCreated = DateZero()
  @observable workspace = Workspace.none
  @observable dateModified = DateZero()
  @observable defaultBillingMethodId: Id | null = Id.none
  @observable defaultPayoutMethodId: Id | null = Id.none

  billingMethods = collection<BillingMethodDTO, BillingMethod>({
    create: () => new BillingMethod(this, this.deleteBillingMethod),
  })

  static get none() {
    return lazyNone()
  }

  constructor(
    private getCurrentMember: GetCurrentMember,
    private getTeamMember: GetTeamMember,
    private primeWorkspace: PrimeWorkspace,
    private deleteBillingMethod: (method: BillingMethod) => Promise<unknown>,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Whether the current user is allowed to manage this billing profile.
   */
  @computed
  get allowedToManage() {
    const member = this.getCurrentMember(this.workspace.id)
    if (member.hasPermission('ADMIN_BILLING_MANAGE')) {
      return true
    }

    if (this.teamId) {
      return Boolean(
        this.getTeamMember(this.teamId, member.user.publicId)?.hasPermission(
          'MANAGE_TEAM_BILLING',
        ),
      )
    }

    if (this.userId) {
      return this.userId === member.user.id
    }

    return false
  }

  parse(json: Partial<BillingProfileDTO>) {
    if (json.billing_methods) {
      json.billing_methods.forEach((method) => {
        this.billingMethods.set(method)
      })
    }
    return {
      id: json.id,
      workspace: this.primeWorkspace({ id: json.workspace_id }),
      defaultBillingMethodId: json.default_billing_method_id,
      defaultPayoutMethodId: json.default_payout_method_id,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      userId: json.user_id,
      userPublicId: json.user_public_id,
      workspaceId: json.workspace_id,
      referralCouponCode: json.referral_coupon_code,
      referralCouponId: json.referral_coupon_id,
      teamId: json.team_id,
    }
  }
}

const lazyNone = once(
  () =>
    new BillingProfile(
      () => Member.none,
      () => TeamMember.none,
      () => Workspace.none,
      () => Promise.resolve(),
    ),
)
